import { useEffect } from 'react';

export default function ParticleCanvas({ darkMode }) {
  useEffect(() => {
    const canvas = document.getElementById('particle-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let mouseX = -1000;
    let mouseY = -1000;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    let particlesArray = [];
    const numberOfParticles = Math.min(Math.floor(window.innerWidth / 15), 100);

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2.5 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.baseAlpha = Math.random() * 0.5 + 0.3;
        this.alpha = this.baseAlpha;
        this.hue = darkMode ? (40 + Math.random() * 20) : (35 + Math.random() * 15);
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;

        // Mouse interaction
        const dx = this.x - mouseX;
        const dy = this.y - mouseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150) {
          this.alpha = Math.min(1, this.baseAlpha + (1 - dist / 150) * 0.5);
          const force = (150 - dist) / 150 * 0.3;
          this.x += dx / dist * force;
          this.y += dy / dist * force;
        } else {
          this.alpha += (this.baseAlpha - this.alpha) * 0.05;
        }
      }
      draw() {
        const sat = darkMode ? '80%' : '75%';
        const light = darkMode ? '55%' : '50%';
        ctx.fillStyle = `hsla(${this.hue}, ${sat}, ${light}, ${this.alpha})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const init = () => {
      particlesArray = [];
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
        for (let j = i + 1; j < particlesArray.length; j++) {
          const dx = particlesArray[i].x - particlesArray[j].x;
          const dy = particlesArray[i].y - particlesArray[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.15;
            const gradient = ctx.createLinearGradient(
              particlesArray[i].x, particlesArray[i].y,
              particlesArray[j].x, particlesArray[j].y
            );
            if (darkMode) {
              gradient.addColorStop(0, `rgba(255, 193, 7, ${opacity})`);
              gradient.addColorStop(1, `rgba(249, 115, 22, ${opacity * 0.5})`);
            } else {
              gradient.addColorStop(0, `rgba(211, 158, 0, ${opacity})`);
              gradient.addColorStop(1, `rgba(217, 119, 6, ${opacity * 0.5})`);
            }
            ctx.beginPath();
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 0.8;
            ctx.moveTo(particlesArray[i].x, particlesArray[i].y);
            ctx.lineTo(particlesArray[j].x, particlesArray[j].y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const handleMouseLeave = () => {
      mouseX = -1000;
      mouseY = -1000;
    };

    init();
    animate();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, [darkMode]);

  return <canvas id="particle-canvas" className="particle-canvas" />;
}
